/*
 * Pure helpers for the player profile. Everything is computed from recorded
 * values; per-90 figures need a stated minutes base and are never extrapolated
 * from a handful of minutes. No percentiles or league-relative scores exist.
 */

const fin = (v) => (v === null || v === undefined || v === "" || !Number.isFinite(Number(v)) ? null : Number(v));

export function positionTag(raw = "") {
  const r = String(raw).toLowerCase();
  if (!r) return "";
  if (r.includes("goalkeeper") || r === "g" || r === "gk") return "GK";
  if (r.includes("defender") || r === "d" || r === "df") return "DF";
  if (r.includes("midfielder") || r === "m" || r === "mf") return "MF";
  if (r.includes("attacker") || r.includes("forward") || r.includes("striker") || r.includes("wing") || r === "f" || r === "fw") return "FW";
  return "";
}

/** Competition with the most minutes: the context for per-90 indicators. */
export function mainCompetition(competitions = []) {
  const withMinutes = competitions.map((c) => ({ c, m: fin(c?.games?.minutes ?? c?.minutes) || 0 })).filter((x) => x.m > 0);
  if (!withMinutes.length) return null;
  return withMinutes.sort((a, b) => b.m - a.m)[0].c;
}

export const MIN_PER90_MINUTES = 180;

export function per90(value, minutes) {
  const v = fin(value);
  const m = fin(minutes);
  if (v === null || m === null || m < MIN_PER90_MINUTES) return null;
  return (v / m) * 90;
}

const IND = {
  goals: (c) => fin(c?.goals?.total),
  assists: (c) => fin(c?.assists ?? c?.goals?.assists),
  shots: (c) => fin(c?.shots?.total),
  sot: (c) => fin(c?.shots?.on),
  saves: (c) => fin(c?.goals?.saves),
  conceded: (c) => fin(c?.goals?.conceded),
  yellow: (c) => fin(c?.cards?.yellow),
};
const LABEL = { goals: "Goals", assists: "Assists", shots: "Shots", sot: "Shots on target", saves: "Saves", conceded: "Goals conceded", yellow: "Yellow cards" };

/** Which recorded fields tell the story for each role. */
const BY_ROLE = {
  FW: ["goals", "assists", "shots", "sot"],
  MF: ["assists", "goals", "shots", "yellow"],
  DF: ["yellow", "goals", "assists", "shots"],
  GK: ["saves", "conceded"],
  "": ["goals", "assists", "shots", "yellow"],
};

/**
 * Position-specific indicators for one competition, as raw totals and per-90.
 * Labelled with their scope; deliberately not scored against other players.
 */
export function positionIndicators(pos, competition) {
  if (!competition) return { scope: "", minutes: null, rows: [] };
  const minutes = fin(competition?.games?.minutes ?? competition?.minutes);
  const keys = BY_ROLE[positionTag(pos)] || BY_ROLE[""];
  const rows = keys
    .map((k) => {
      const total = IND[k](competition);
      return total === null ? null : { key: k, label: LABEL[k], total, per90: per90(total, minutes) };
    })
    .filter(Boolean);
  return { scope: competition.league || "", minutes, rows };
}

const num = (v) => fin(v);

/** Compact recent-form rows from the provider game log (newest first). */
export function recentForm(games = [], n = 5) {
  return games.slice(0, n).map((g) => ({
    fixtureId: g.fixture_id,
    date: g.date,
    opponent: g.opponent || "—",
    competition: g.competition || "",
    home: g.is_home,
    score: g.score || "",
    result: g.result || "",
    minutes: num(g.minutes),
    rating: num(g.rating),
    goals: num(g.goals),
    assists: num(g.assists),
    shots: num(g.shots),
    yellow: num(g.yellow),
    red: num(g.red),
  }));
}

/** Average rating over games with a recorded rating and minutes > 0. */
export function averageRating(games = []) {
  const vals = games.filter((g) => num(g.rating) !== null && (num(g.minutes) || 0) > 0).map((g) => Number(g.rating));
  return vals.length ? { value: vals.reduce((a, b) => a + b, 0) / vals.length, n: vals.length } : null;
}

/** The row for one match, matched on the provider fixture id (never the CSB id). */
export const gameForFixture = (games = [], providerFixtureId) =>
  providerFixtureId ? games.find((g) => Number(g.fixture_id) === Number(providerFixtureId)) || null : null;

export const chartSeries = {
  shots: { label: "Shots", pick: (g) => num(g.shots), second: { label: "On target", pick: (g) => num(g.sot) } },
  contributions: { label: "Goals", pick: (g) => num(g.goals), second: { label: "Assists", pick: (g) => num(g.assists) } },
  rating: { label: "Rating", pick: (g) => num(g.rating), decimals: 1 },
  fouls: { label: "Fouls committed", pick: (g) => num(g.fouls_committed), second: { label: "Fouls drawn", pick: (g) => num(g.fouls_drawn) } },
};
